import { AbstractControl } from '@angular/forms';

export interface SelectOption {
  label: string;
  value: string;
}

/**
 * Returns the message for the control's first active validation error, but only
 * once the user has interacted with the field (touched or dirty) — so errors
 * don't flash before the user has had a chance to type anything.
 */
export function fieldErrorMessage(
  control: AbstractControl | null | undefined,
  messages: Record<string, string>,
): string | null {
  if (!control || control.valid || !(control.touched || control.dirty)) {
    return null;
  }
  const errorKey = Object.keys(control.errors ?? {})[0];
  return errorKey ? (messages[errorKey] ?? 'This value is not valid.') : null;
}

export const PREFERRED_CONTACT_OPTIONS: SelectOption[] = [
  { label: 'Email', value: 'email' },
  { label: 'Phone', value: 'phone' },
  { label: 'Either', value: 'either' },
];

export const US_STATE_OPTIONS: SelectOption[] = [
  { label: 'California', value: 'CA' },
  { label: 'Oregon', value: 'OR' },
  { label: 'Washington', value: 'WA' },
  { label: 'Nevada', value: 'NV' },
  { label: 'Arizona', value: 'AZ' },
  { label: 'Colorado', value: 'CO' },
  { label: 'Texas', value: 'TX' },
  { label: 'Other', value: 'OTHER' },
];

export const US_COUNTY_OPTIONS: SelectOption[] = [
  { label: 'Alameda', value: 'ALAMEDA' },
  { label: 'Los Angeles', value: 'LOS_ANGELES' },
  { label: 'Orange', value: 'ORANGE' },
  { label: 'San Bernardino', value: 'SAN_BERNARDINO' },
  { label: 'Chino', value: 'CHINO' },
  { label: 'San Diego', value: 'SAN_DIEGO' },
  { label: 'San Francisco', value: 'SAN_FRANCISCO' },
  { label: 'Santa Clara', value: 'SANTA_CLARA' },
  { label: 'Other', value: 'OTHER' },
];

export const PROPERTY_TYPE_OPTIONS: SelectOption[] = [
  { label: 'Single-Family Home', value: 'SINGLE_FAMILY' },
  { label: 'Multi-Family Home', value: 'MULTI_FAMILY' },
  { label: 'Townhouse', value: 'TOWNHOUSE' },
  { label: 'Condominium', value: 'CONDO' },
  { label: 'Vacant Land', value: 'VACANT_LAND' },
  { label: 'Other', value: 'OTHER' },
];

// Values match backend seed exactly (INTAKE_TIMELINE question options)
export const TIMELINE_OPTIONS: SelectOption[] = [
  { label: 'As soon as possible', value: 'asap' },
  { label: 'Within 3 months', value: 'within_3mo' },
  { label: '3–6 months', value: '3_6mo' },
  { label: '6–12 months', value: '6_12mo' },
  { label: 'Just exploring', value: 'exploring' },
];

// Values match backend seed exactly (INTAKE_BUDGET question options)
export const BUDGET_OPTIONS: SelectOption[] = [
  { label: 'Under $100K', value: 'under_100k' },
  { label: '$100K – $200K', value: '100k_200k' },
  { label: '$200K – $300K', value: '200k_300k' },
  { label: 'Above $300K', value: 'above_300k' },
  { label: 'Prefer not to disclose', value: 'prefer_not' },
];

// Values match backend seed exactly (INTAKE_STAGE question options)
export const CURRENT_STAGE_OPTIONS: SelectOption[] = [
  { label: 'Exploring options', value: 'exploring' },
  { label: 'Planning', value: 'planning' },
  { label: 'Already contracted', value: 'contracted' },
  { label: 'Other', value: 'other' },
];

// Values match backend seed exactly (INTAKE_INTENDED_USE question options)
export const INTENDED_USE_OPTIONS: SelectOption[] = [
  { label: 'Wildfire Rebuild', value: 'wildfire_rebuild' },
  { label: 'Rental Income / Investment', value: 'rental_income' },
  { label: 'Family / Multigenerational Living', value: 'family_multigenerational' },
  // { label: 'Guest House', value: 'guest_house' },
  // { label: 'Workforce Housing', value: 'workforce_housing' },
  // { label: 'University-affiliated Housing', value: 'university_housing' },
  // { label: 'ADU Development', value: 'adu_development' },
  { label: 'Other', value: 'other' },
];

// Values match backend seed exactly (INTAKE_PRIMARY_INTEREST question options)
export const PRIMARY_INTEREST_OPTIONS: SelectOption[] = [
  { label: 'Faster Rebuild', value: 'faster_rebuild' },
  { label: 'Transparent Pricing', value: 'transparent_pricing' },
  { label: 'Design-Build Coordination', value: 'design_build' },
  { label: 'Permitting Support', value: 'permitting_support' },
  { label: 'Cost Clarity', value: 'cost_clarity' },
  { label: 'Sustainable-Resilient Design', value: 'sustainable_design' },
  { label: 'Expert Guidance', value: 'expert_guidance' },
];

export const ABOUT_ADU_OPTIONS: SelectOption[] = [
  { label: 'Google Search', value: 'google' },
  { label: 'Social Media', value: 'social_media' },
  { label: 'YouTube', value: 'youtube' },
  // { label: 'Friend / Family Referral', value: 'referral' },
  // { label: 'Real Estate Agent', value: 'real_estate' },
  // { label: 'Contractor / Architect', value: 'contractor' },
  // { label: 'Local News / Media', value: 'local_news' },
  // { label: 'Community Event / Home Show', value: 'community_event' },
  // { label: 'Previous Customer Referral', value: 'previous_customer' },
];

export const YES_NO_OPTIONS: SelectOption[] = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

export const WILDFIRE_OPTIONS: SelectOption[] = [
  { label: 'Altadena', value: 'altadena' },
  { label: 'Pasadena', value: 'pasadena' },
  { label: 'Eaton', value: 'eaton' },
];

export interface PhotoSlotDef {
  key: string;
  label: string;
  hint: string;
}

export const PHOTO_SLOTS: PhotoSlotDef[] = [
  { key: 'frontView', label: 'Front View', hint: 'Include street number' },
  { key: 'backyard', label: 'Backyard', hint: 'Key build space' },
  { key: 'sideYard', label: 'Side Yard', hint: 'Access pathways' },
  { key: 'existingHouse', label: 'Existing House', hint: 'Main structure' },
  { key: 'otherLot', label: 'Other / Lot', hint: 'Any constraints' },
];

export interface DocumentSlotDef {
  key: string;
  label: string;
}

export const DOCUMENT_SLOTS: DocumentSlotDef[] = [
  { key: 'propertySurvey', label: 'Property Survey' },
  { key: 'sitePlan', label: 'Site Plan' },
  { key: 'architecturalDrawings', label: 'Architectural Drawings' },
  { key: 'permitDocuments', label: 'Permit Documents' },
];

export interface PropertyIntakeUploads {
  photos: Record<string, File | null>;
  documents: Record<string, File | null>;
}

export function createEmptyUploads(): PropertyIntakeUploads {
  return {
    photos: Object.fromEntries(PHOTO_SLOTS.map(slot => [slot.key, null])),
    documents: Object.fromEntries(DOCUMENT_SLOTS.map(slot => [slot.key, null])),
  };
}

export function optionLabel(options: SelectOption[], value: string | null | undefined): string {
  return options.find(option => option.value === value)?.label ?? '—';
}

export function resolveStateOption(shortCode: string | null | undefined): string {
  return US_STATE_OPTIONS.some(option => option.value === shortCode) ? (shortCode as string) : 'OTHER';
}

export function resolveCountyOption(name: string | null | undefined): string | null {
  if (!name) {
    return null;
  }
  const match = US_COUNTY_OPTIONS.find(option => option.label.toLowerCase() === name.toLowerCase());
  return match ? match.value : 'OTHER';
}
